import { ReactComponent as Location } from "../../shared/assets/location.svg";
import { themeAtom } from "../../shared/atoms";
import Map from "../../shared/components/Map";
import { api, apiUrl } from "../../shared/services/service";
import { MapInfo } from "../../tech-form/models/FormInterfaces";
import DeliveryPlan from "./DeliveryPlan";
import PreDeliveryPlan from "./PreDeliveryPlan";
import ReDeliveryPlan from "./ReDeliveryPlan";
import { Tab, Tabs, TabsHandle } from "./TabControl";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface ChecklistTabProps {
  mapData: MapInfo | null;
  className?: string;
}

const ChecklistTab: React.FC<ChecklistTabProps> = ({ mapData, className }) => {
  const theme = useAtomValue(themeAtom);

  const currentPosition = mapData?.["Current position"];
  const { tcCode, voyageNo } = useParams();
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const tabRef = useRef<TabsHandle>(null);

  useEffect(() => {
    if (currentPosition) {
      const coordinates = currentPosition.split(" ").map((coordinate) => {
        return parseFloat(coordinate.replace(/[ºNºW]/g, ""));
      });

      if (coordinates.length === 2 && !isNaN(coordinates[0]) && !isNaN(coordinates[1])) {
        setLatitude(coordinates[0]);
        setLongitude(coordinates[1]);
      } else {
        setLatitude(null);
        setLongitude(null);
      }
    } else {
      setLatitude(null);
      setLongitude(null);
    }
  }, [currentPosition]);

  const fetchData = useCallback(async () => {
    try {
      const status = await api.get(`${apiUrl}/gettcchecklistrealtimestatus`, {
        params: {
          tc_code: tcCode,
          voyage_no: voyageNo,
        },
      });
      return status.data[0]?.TIMECHARTERCONTRACT_STATUS;
    } catch (error: unknown) {
      console.error("Error fetching data:", error);
    }
  }, [tcCode, voyageNo]);

  useEffect(() => {
    fetchData().then((data) => {
      if (!data) return;
      tabRef.current?.setActiveTab(data);
    });
  }, [fetchData]);

  return (
    <div className={twMerge("h-full overflow-auto p-2", className)}>
      <Tabs ref={tabRef}>
        <Tab label="Pre delivery plan">
          <PreDeliveryPlan />
        </Tab>
        <Tab label="Delivery">
          <DeliveryPlan />
        </Tab>
        <Tab label="Redelivery">
          <ReDeliveryPlan />
        </Tab>
      </Tabs>

      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <Location
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "16px",
              color: theme.myVesselsTable.searchIcon.innerFill,
            }}
          />
          <p className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>Current leg</p>
        </div>

        {latitude !== null && longitude !== null ? (
          <Map
            center={{ lat: latitude, lng: longitude }}
            zoom={3}
            shipCoordinates={{ lat: latitude, lng: longitude }}
          />
        ) : (
          <p>No data</p>
        )}

        {mapData && (
          <div className="grid grid-cols-3 gap-x-4 gap-y-6">
            {Object.entries(mapData).map(([key, value]) => (
              <div key={key} className="flex flex-col gap-3">
                <div className="text-xs text-gray-400">{key}</div>
                <div className={twMerge("text-sm", theme.uwhcAssistant.text)}>{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistTab;
